import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BookText,
  ChevronDown,
  ChevronRight,
  FileCode,
  FilePlus,
  FileText,
  Folder,
  FolderPlus,
  Layers3,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Puzzle,
  Trash2,
  Upload,
  Wrench,
  X,
} from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { MarkdownRenderer } from 'features/chat/agent-chat-module/components/markdown-renderer/markdown-renderer';
import { HttpError } from 'lib/https';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { SkillToolsSection } from './components/skill-tools-section';
import { useGetSkillById, useUpdateSkill, useUpdateSkillResource } from './use-skills';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { SkillDetails, SkillResource, UpdateSkillPayload } from './skills.types';
import {
  ALLOWED_SKILL_FILE_HINT,
  SkillFolderNode,
  buildSkillFolderTree,
  collectFolderPaths,
  makeFolderPlaceholder,
  skillContentType,
  skillResourceLanguage,
  skillResourceType,
  withoutFolderPlaceholder,
} from './skill-resource';
import { Edit } from 'iconoir-react';
import { DiscardChangesModal } from '../models/discard-changes-modal';

type SkillFileKind = 'markdown' | 'code';

type SkillFile = {
  name: string;
  path: string;
  content: string;
  language?: string | null;
  kind: SkillFileKind;
  fileId?: string;
};

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpError) {
    const body = error.error;
    if (body && typeof body === 'object') {
      const issues = (body as { issues?: Array<{ message?: string }> }).issues;
      if (Array.isArray(issues) && issues[0]?.message) {
        return issues[0].message;
      }
      if (typeof (body as { detail?: unknown }).detail === 'string') {
        return (body as { detail: string }).detail;
      }
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function skillResourcesPayload(data: SkillDetails, resources: SkillResource[]): UpdateSkillPayload {
  return {
    name: data.name,
    description: data.description || '',
    instructions: data.instructions || '',
    version: data.version || '1.0.0',
    iconUrl: data.iconUrl,
    tags: data.tags || [],
    requiredToolIds: data.requiredToolIds || [],
    requiredIntegrationTypes: data.requiredIntegrationTypes || [],
    scope: (data.scope as 'personal') || 'personal',
    visibility: data.visibility || 'public',
    resources,
  };
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getSkillInitials = (name?: string | null) => {
  if (!name) {
    return 'SK';
  }

  const parts = name.split(/[\s-_]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'SK';
};

const getSkillDescription = (
  skill: SkillDetails | null | undefined,
  translate: (key: string) => string
) => {
  if (!skill) {
    return '';
  }

  const description = skill.description?.trim();
  if (description) {
    return description;
  }

  return (
    translate('SKILLS_NO_DESCRIPTION') ||
    'No description available for this skill yet.'
  );
};

const getResourceKind = (resource: SkillResource): SkillFileKind => {
  const fileName = resource.fileName.toLowerCase();

  if (resource.contentType?.includes('markdown') || fileName.endsWith('.md')) {
    return 'markdown';
  }

  return 'code';
};

const getResourceLanguage = (resource: SkillResource) => {
  if (resource.language) {
    return resource.language;
  }

  const extension = resource.fileName.split('.').pop()?.toLowerCase();

  if (extension === 'py') {
    return 'python';
  }

  if (extension === 'ts' || extension === 'tsx') {
    return 'typescript';
  }

  if (extension === 'js' || extension === 'jsx') {
    return 'javascript';
  }

  return extension || 'text';
};

const buildSkillFiles = (skill?: SkillDetails | null): SkillFile[] => {
  const files: SkillFile[] = [
    {
      name: 'SKILL.md',
      path: 'SKILL.md',
      content: skill?.instructions || '',
      kind: 'markdown',
      language: 'markdown',
    },
  ];

  const resources = Array.isArray(skill?.resources) ? skill.resources : [];

  resources.forEach((resource) => {
    if (!resource?.path || !resource.fileName) {
      return;
    }

    files.push({
      name: resource.fileName,
      path: resource.path,
      content: resource.content || '',
      kind: getResourceKind(resource),
      language: getResourceLanguage(resource),
      fileId: resource.fileId,
    });
  });

  return files;
};

const renderFileContent = (file?: SkillFile) => {
  if (!file?.content) {
    return '_No content available._';
  }

  if (file.kind === 'code') {
    return `\`\`\`${file.language || 'text'}\n${file.content}\n\`\`\``;
  }

  return file.content;
};

function SkillDetailsLoadingState() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b px-5 py-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col lg:grid lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="border-b p-4 lg:border-b-0 lg:border-r">
            <Skeleton className="mb-4 h-3 w-16" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          <div className="space-y-4 p-5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SkillDetailsErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 p-6 text-center">
      <div className="rounded-full bg-muted p-3 text-muted-foreground">
        <BookText className="h-5 w-5" />
      </div>
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">
          {t('SKILL_DETAILS_LOAD_ERROR_TITLE') || 'Unable to load skill'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('SKILL_DETAILS_LOAD_ERROR_DESCRIPTION') || 'Please try again in a moment.'}
        </p>
      </div>
      <Button variant="outline" size="sm" className="h-8 px-3" onClick={onRetry}>
        {t('SKILLS_TRY_AGAIN') || 'Try again'}
      </Button>
    </div>
  );
}

function NewFileModal({
  open,
  onOpenChange,
  existingFolders,
  initialFolder = '',
  onSave,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingFolders: string[];
  initialFolder?: string;
  onSave: (fileName: string, folder: string, content: string) => Promise<boolean>;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState('');
  const [folder, setFolder] = useState(initialFolder);
  const [content, setContent] = useState('');

  useEffect(() => {
    if (open) {
      setFolder(initialFolder);
    }
  }, [open, initialFolder]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setFileName('');
      setFolder('');
      setContent('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileName.trim()) return;
    const created = await onSave(fileName.trim(), folder, content);
    if (created) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px]" nested>
        <DialogHeader>
          <DialogTitle>{t('NEW_FILE') || 'New file'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-file-name">{t('FILE_NAME') || 'File name'}</Label>
            <Input
              id="new-file-name"
              placeholder={`e.g. script.py or notes.md (${ALLOWED_SKILL_FILE_HINT})`}
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-file-folder">{t('FOLDER') || 'Folder (optional)'}</Label>
            <Input
              id="new-file-folder"
              placeholder={t('SKILL_FOLDER_PLACEHOLDER', {
                defaultValue: 'e.g. scripts or references',
              })}
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              list="existing-folders-list"
            />
            {existingFolders.length > 0 && (
              <datalist id="existing-folders-list">
                {existingFolders.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={!fileName.trim() || isPending}>
              {isPending ? (t('SAVING') || 'Saving...') : (t('CREATE') || 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewFolderModal({
  open,
  onOpenChange,
  onSave,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (folderName: string) => Promise<boolean>;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState('');

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setFolderName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderName.trim()) return;
    const created = await onSave(folderName.trim());
    if (created) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]" nested>
        <DialogHeader>
          <DialogTitle>{t('NEW_FOLDER') || 'New folder'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-folder-name">{t('FOLDER_NAME') || 'Folder name'}</Label>
            <Input
              id="new-folder-name"
              placeholder={t('SKILL_FOLDER_PLACEHOLDER', {
                defaultValue: 'e.g. scripts or references',
              })}
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={!folderName.trim() || isPending}>
              {isPending ? (t('SAVING') || 'Saving...') : (t('CREATE') || 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UploadFileModal({
  open,
  onOpenChange,
  existingFolders,
  initialFolder = '',
  onUpload,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingFolders: string[];
  initialFolder?: string;
  onUpload: (files: { file: File; folder: string }[]) => Promise<boolean>;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folder, setFolder] = useState(initialFolder);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    if (open) {
      setFolder(initialFolder);
    }
  }, [open, initialFolder]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setSelectedFiles([]);
      setFolder('');
      setIsDragActive(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    if (e.dataTransfer.files) {
      setSelectedFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;
    const items = selectedFiles.map((file) => ({ file, folder }));
    const uploaded = await onUpload(items);
    if (uploaded) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]" nested>
        <DialogHeader>
          <DialogTitle>{t('UPLOAD_FILE') || 'Upload file'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div
            className={cn(
              'flex min-h-[140px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors',
              isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            )}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={handleDrop}
            onClick={() => document.getElementById('skill-file-upload-input')?.click()}
          >
            <input
              id="skill-file-upload-input"
              type="file"
              multiple
              accept=".py,.md,.txt,.csv,.json,.html,.xml,.xsd"
              className="hidden"
              onChange={handleFileInput}
            />
            <Upload className="mb-2 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              {selectedFiles.length > 0
                ? t('FILES_SELECTED', {
                  count: selectedFiles.length,
                  defaultValue: '{{count}} file selected',
                })
                : t('DRAG_AND_DROP_OR_CLICK') || 'Drag & drop files here, or click to select'}
            </p>
            {selectedFiles.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {selectedFiles.map((f) => f.name).join(', ')}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="upload-folder">{t('TARGET_FOLDER') || 'Target folder (optional)'}</Label>
            <Input
              id="upload-folder"
              placeholder={t('SKILL_FOLDER_PLACEHOLDER', {
                defaultValue: 'e.g. scripts or references',
              })}
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              list="upload-existing-folders-list"
            />
            {existingFolders.length > 0 && (
              <datalist id="upload-existing-folders-list">
                {existingFolders.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={selectedFiles.length === 0 || isPending}>
              {isPending ? (t('UPLOADING') || 'Uploading...') : (t('UPLOAD') || 'Upload')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RenameFileModal({
  open,
  onOpenChange,
  file,
  onRename,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: SkillFile | null;
  onRename: (file: SkillFile, newName: string) => Promise<boolean>;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (file) {
      setNewName(file.name);
    }
  }, [file]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setNewName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !newName.trim() || newName.trim() === file.name) return;
    const renamed = await onRename(file, newName.trim());
    if (renamed) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]" nested>
        <DialogHeader>
          <DialogTitle>{t('RENAME_FILE') || 'Rename'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rename-file-input">{t('FILE_NAME') || 'File name'}</Label>
            <Input
              id="rename-file-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={!newName.trim() || newName.trim() === file?.name || isPending}>
              {isPending ? (t('SAVING') || 'Saving...') : (t('RENAME') || 'Rename')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteFileModal({
  open,
  onOpenChange,
  file,
  onDelete,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: SkillFile | null;
  onDelete: (file: SkillFile) => Promise<void>;
  isPending: boolean;
}) {
  const { t } = useTranslation();

  const handleDelete = async () => {
    if (!file) return;
    await onDelete(file);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" nested>
        <DialogHeader>
          <DialogTitle className="text-red-600 dark:text-red-400">{t('DELETE_FILE') || 'Delete file'}</DialogTitle>
        </DialogHeader>
        <div className="py-2 text-sm text-muted-foreground">
          {t('DELETE_FILE_CONFIRMATION', {
            name: file?.name,
            defaultValue: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
          })}
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('CANCEL') || 'Cancel'}
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isPending}>
            {isPending ? (t('DELETING') || 'Deleting...') : (t('DELETE') || 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Loaded skill plus the save and delete actions. The modal does not choose the API. */
export type SkillDetailsSource = {
  variant: 'learned';
  skill: SkillDetails | null;
  isLoading: boolean;
  isError: boolean;
  isSaving?: boolean;
  onRetry: () => void;
  onUpdate: (payload: UpdateSkillPayload) => Promise<void>;
  onDelete?: () => void;
  banner?: ReactNode;
  meta?: string;
  note?: string;
};

export function SkillDetailsModal({
  skillId,
  open,
  onClose,
  source,
}: {
  skillId: string | null;
  open: boolean;
  onClose: () => void;
  source?: SkillDetailsSource;
}) {
  const { t } = useTranslation();
  const catalogueSkill = useGetSkillById(skillId ?? '', open && !source);
  const { mutateAsync: updateCatalogueSkill, isPending: isUpdatingCatalogue } = useUpdateSkill();
  const { mutateAsync: updateSkillResource, isPending: isUpdatingResource } = useUpdateSkillResource();
  const data = source ? source.skill : catalogueSkill.data;
  const isLoading = source ? source.isLoading : catalogueSkill.isLoading;
  const isError = source ? source.isError : catalogueSkill.isError;
  const refetch = source ? source.onRetry : () => void catalogueSkill.refetch();
  const isLearned = source?.variant === 'learned';
  const isOwner = useIsOwner(source ? undefined : data);
  const canManage = source ? Boolean(data) : Boolean(data && isOwner && !data.isSystem);
  const isUpdating = source ? Boolean(source.isSaving) : isUpdatingCatalogue;
  const updateSkill = async (args: { skillId: string; payload: UpdateSkillPayload }) => {
    if (source) {
      await source.onUpdate(args.payload);
      return;
    }
    await updateCatalogueSkill(args);
  };
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState('SKILL.md');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [pendingFile, setPendingFile] = useState<string | null>(null);
  const [pendingClose, setPendingClose] = useState(false);
  const [isNewFileModalOpen, setIsNewFileModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [targetFolderForNew, setTargetFolderForNew] = useState('');
  const [renameFileTarget, setRenameFileTarget] = useState<SkillFile | null>(null);
  const [deleteFileTarget, setDeleteFileTarget] = useState<SkillFile | null>(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [creatingInlineFile, setCreatingInlineFile] = useState<{ folder: string } | null>(null);
  const [inlineFileName, setInlineFileName] = useState('');
  const [creatingInlineFolder, setCreatingInlineFolder] = useState<{ parentFolder: string } | null>(null);
  const [inlineFolderName, setInlineFolderName] = useState('');
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(() => new Set());
  const [customResources, setCustomResources] = useState<SkillResource[] | null>(null);
  const inlineInputMountTimeRef = useRef(0);
  const inlineSubmitLockRef = useRef(false);

  const focusInlineNameInput = (el: HTMLInputElement | null) => {
    if (!el) return;
    window.setTimeout(() => {
      el.focus();
    }, 0);
  };

  const preventMenuFocusRestore = (event: Event) => {
    event.preventDefault();
  };

  const expandFolderPath = (path: string) => {
    if (!path) return;
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      const parts = path.split('/').filter(Boolean);
      for (let i = 1; i <= parts.length; i++) {
        next.delete(parts.slice(0, i).join('/'));
      }
      return next;
    });
  };

  const toggleFolder = (path: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const openInlineFile = (folder: string) => {
    expandFolderPath(folder);
    inlineInputMountTimeRef.current = Date.now();
    inlineSubmitLockRef.current = false;
    setCreatingInlineFile({ folder });
    setInlineFileName('');
  };

  const openInlineFolder = (parentFolder: string) => {
    expandFolderPath(parentFolder);
    inlineInputMountTimeRef.current = Date.now();
    inlineSubmitLockRef.current = false;
    setCreatingInlineFolder({ parentFolder });
    setInlineFolderName('');
  };

  useEffect(() => {
    if (open) {
      setSelectedFile('SKILL.md');
      setIsEditMode(false);
      setIsEditingDescription(false);
      setEditDescription('');
      setIsEditingName(false);
      setEditName('');
      setCreatingInlineFile(null);
      setInlineFileName('');
      setCreatingInlineFolder(null);
      setInlineFolderName('');
      setCollapsedFolders(new Set());
      setCustomResources(null);
    }
  }, [open, skillId]);

  const currentResources = customResources ?? data?.resources ?? [];
  const currentResourcesRef = useRef(currentResources);
  currentResourcesRef.current = currentResources;
  const files = useMemo(
    () => buildSkillFiles(data ? { ...data, resources: currentResources } : null),
    [data, currentResources]
  );

  useEffect(() => {
    const file = files.find((f) => f.path === selectedFile) ?? files[0];
    if (file) {
      setEditContent(file.content ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, data, currentResources]);

  const { rootFiles, folders: folderTree } = useMemo(() => buildSkillFolderTree(files), [files]);
  const activeFile = files.find((file) => file.path === selectedFile) ?? files[0];
  const skillDescription = getSkillDescription(data, t);

  const existingFolderNames = useMemo(() => collectFolderPaths(folderTree), [folderTree]);

  const handleCreateNewFile = async (fileName: string, folderName: string, initialContent: string) => {
    if (!data || !canManage) return false;
    const type = skillResourceType(fileName);
    if (!type) {
      toast({
        variant: 'destructive',
        title: t('UNSUPPORTED_FILE_TYPE', { defaultValue: 'Unsupported File Type' }),
        description: t('ALLOWED_FILE_EXTENSIONS', {
          extensions: ALLOWED_SKILL_FILE_HINT,
          defaultValue: 'Allowed extensions: {{extensions}}.',
        }),
      });
      return false;
    }

    const cleanFolder = folderName.trim().replace(/\/+$|^\/+/g, '');
    const relPath = cleanFolder ? `${cleanFolder}/${fileName}` : fileName;
    const content = initialContent || ' ';
    const newResource: SkillResource = {
      type,
      path: relPath,
      fileId: `file-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      fileName,
      size: new Blob([content]).size,
      contentType: skillContentType(fileName),
      content,
      language: skillResourceLanguage(type, fileName),
    };

    const previous = customResources;
    const updatedResources = [
      ...withoutFolderPlaceholder(
        currentResourcesRef.current.filter((r) => r.path !== relPath),
        cleanFolder
      ),
      newResource,
    ];

    setCustomResources(updatedResources);
    setSelectedFile(relPath);

    try {
      await updateSkill({
        skillId: data.id,
        payload: skillResourcesPayload(data, updatedResources),
      });
      setCustomResources(null);

      toast({
        variant: 'success',
        title: t('FILE_CREATED_TITLE', { defaultValue: 'File created' }),
        description: `${fileName}`,
      });
      return true;
    } catch (error) {
      setCustomResources(previous);
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: apiErrorMessage(error, t('FAILED_TO_CREATE_FILE', { defaultValue: 'Failed to create file.' })),
      });
      return false;
    }
  };

  const handleCreateNewFolder = async (folderName: string) => {
    if (!data || !canManage) return false;
    const cleanFolder = folderName.trim().replace(/\/+$|^\/+/g, '');
    const newResource = makeFolderPlaceholder(cleanFolder);

    const previous = customResources;
    const updatedResources = [
      ...currentResourcesRef.current.filter((r) => r.path !== newResource.path),
      newResource,
    ];

    setCustomResources(updatedResources);

    try {
      await updateSkill({
        skillId: data.id,
        payload: skillResourcesPayload(data, updatedResources),
      });
      toast({
        variant: 'success',
        title: t('FOLDER_CREATED_TITLE', { defaultValue: 'Folder created' }),
        description: `${cleanFolder.split('/').pop() ?? cleanFolder}`,
      });
      openInlineFile(cleanFolder);
      return true;
    } catch (error) {
      setCustomResources(previous);
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: apiErrorMessage(error, t('FAILED_TO_CREATE_FOLDER', { defaultValue: 'Failed to create folder.' })),
      });
      return false;
    }
  };

  const handleUploadFiles = async (items: { file: File; folder: string }[]) => {
    if (!data || !canManage) return false;

    const unsupported = items.filter(({ file }) => !skillResourceType(file.name));
    if (unsupported.length > 0) {
      toast({
        variant: 'destructive',
        title: t('UNSUPPORTED_FILE_TYPE', { defaultValue: 'Unsupported File Type' }),
        description: t('UNSUPPORTED_FILES_ALLOWED_EXTENSIONS', {
          files: unsupported.map((item) => item.file.name).join(', '),
          extensions: ALLOWED_SKILL_FILE_HINT,
          defaultValue: '{{files}}. Allowed: {{extensions}}.',
        }),
      });
      return false;
    }

    const newResources: SkillResource[] = await Promise.all(
      items.map(async ({ file, folder: folderName }) => {
        const textContent = await file.text();
        const cleanFolder = folderName.trim().replace(/\/+$|^\/+/g, '');
        const relPath = cleanFolder ? `${cleanFolder}/${file.name}` : file.name;
        const type = skillResourceType(file.name)!;

        return {
          type,
          path: relPath,
          fileId: `file-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
          fileName: file.name,
          size: file.size,
          contentType: skillContentType(file.name),
          content: textContent,
          language: skillResourceLanguage(type, file.name),
        };
      })
    );

    let updatedResources = [...(currentResources || [])];
    const foldersTouched = new Set(
      items.map(({ folder }) => folder.trim().replace(/\/+$|^\/+/g, ''))
    );
    foldersTouched.forEach((folder) => {
      updatedResources = withoutFolderPlaceholder(updatedResources, folder);
    });

    newResources.forEach((nr) => {
      const idx = updatedResources.findIndex((r) => r.path === nr.path);
      if (idx >= 0) {
        updatedResources[idx] = nr;
      } else {
        updatedResources.push(nr);
      }
    });

    try {
      await updateSkill({
        skillId: data.id,
        payload: skillResourcesPayload(data, updatedResources),
      });

      if (newResources[0]) {
        setSelectedFile(newResources[0].path);
      }

      toast({
        variant: 'success',
        title: t('FILE_UPLOADED_TITLE', { defaultValue: 'Files uploaded' }),
        description: `${newResources.length} ${t('FILE', { defaultValue: 'file' })}`,
      });
      return true;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: t('FAILED_TO_UPLOAD_FILES', { defaultValue: 'Failed to upload files.' }),
      });
      return false;
    }
  };

  const handleRenameFile = async (file: SkillFile, newName: string) => {
    if (!data || !canManage) return false;
    const type = skillResourceType(newName);
    if (!type) {
      toast({
        variant: 'destructive',
        title: t('UNSUPPORTED_FILE_TYPE', { defaultValue: 'Unsupported File Type' }),
        description: t('ALLOWED_FILE_EXTENSIONS', {
          extensions: ALLOWED_SKILL_FILE_HINT,
          defaultValue: 'Allowed extensions: {{extensions}}.',
        }),
      });
      return false;
    }

    const oldPath = file.path;
    const pathParts = oldPath.split('/');
    pathParts[pathParts.length - 1] = newName;
    const newPath = pathParts.join('/');

    const existingResources = currentResources || [];
    const updatedResources = existingResources.map((r) => {
      if (r.path === oldPath) {
        return {
          ...r,
          type,
          fileName: newName,
          path: newPath,
          contentType: skillContentType(newName),
          language: skillResourceLanguage(type, newName),
        };
      }
      return r;
    });

    try {
      await updateSkill({
        skillId: data.id,
        payload: skillResourcesPayload(data, updatedResources),
      });

      if (selectedFile === oldPath) {
        setSelectedFile(newPath);
      }

      toast({
        variant: 'success',
        title: t('FILE_RENAMED_TITLE', { defaultValue: 'File renamed' }),
        description: `${newName}`,
      });
      return true;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: t('FAILED_TO_RENAME_FILE', { defaultValue: 'Failed to rename file.' }),
      });
      return false;
    }
  };

  const handleDeleteFile = async (file: SkillFile) => {
    if (!data || !canManage) return;
    const targetPath = file.path;
    const existingResources = currentResources || [];
    const updatedResources = existingResources.filter((r) => r.path !== targetPath);

    try {
      await updateSkill({
        skillId: data.id,
        payload: skillResourcesPayload(data, updatedResources),
      });

      if (selectedFile === targetPath) {
        setSelectedFile('SKILL.md');
        setIsEditMode(false);
      }

      toast({
        variant: 'success',
        title: t('FILE_DELETED_TITLE', { defaultValue: 'File deleted' }),
        description: `${file.name}`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: t('FAILED_TO_DELETE_FILE', { defaultValue: 'Failed to delete file.' }),
      });
    }
  };

  const handleInlineFileSubmit = async () => {
    if (!creatingInlineFile || inlineSubmitLockRef.current) return;
    const fileName = inlineFileName.trim();
    const folderName = creatingInlineFile.folder;

    if (!fileName) {
      setCreatingInlineFile(null);
      setInlineFileName('');
      return;
    }

    inlineSubmitLockRef.current = true;
    try {
      const created = await handleCreateNewFile(fileName, folderName, '');
      if (created) {
        setCreatingInlineFile(null);
        setInlineFileName('');
      }
    } finally {
      inlineSubmitLockRef.current = false;
    }
  };

  const handleInlineFolderSubmit = async () => {
    if (!creatingInlineFolder || inlineSubmitLockRef.current) return;
    const folderName = inlineFolderName.trim();
    const parentFolder = creatingInlineFolder.parentFolder;

    if (!folderName) {
      setCreatingInlineFolder(null);
      setInlineFolderName('');
      return;
    }

    inlineSubmitLockRef.current = true;
    try {
      const fullFolderPath = parentFolder ? `${parentFolder}/${folderName}` : folderName;
      const created = await handleCreateNewFolder(fullFolderPath);
      if (created) {
        setCreatingInlineFolder(null);
        setInlineFolderName('');
      }
    } finally {
      inlineSubmitLockRef.current = false;
    }
  };

  const isFileDirty = isEditMode && editContent !== (activeFile?.content ?? '');
  const isDescriptionDirty =
    isEditingDescription && editDescription.trim() !== (data?.description?.trim() || '');
  const isNameDirty = isEditingName && editName.trim() !== (data?.name?.trim() || '');
  const isDirty = isFileDirty || isDescriptionDirty || isNameDirty;

  const handleFileSelect = (path: string) => {
    if (path === selectedFile) return;
    if (isDirty) {
      setPendingFile(path);
      setShowDiscardWarning(true);
    } else {
      setSelectedFile(path);
      setIsEditMode(false);
    }
  };

  const handleStartEditDescription = () => {
    setEditDescription(data?.description?.trim() || '');
    setIsEditingDescription(true);
  };

  const handleDescriptionSave = async () => {
    if (!data || !canManage || !isDescriptionDirty) return;

    try {
      await updateSkill({
        skillId: data.id,
        payload: {
          name: data.name,
          description: editDescription.trim(),
          instructions: data.instructions || '',
          version: data.version || '1.0.0',
          iconUrl: data.iconUrl,
          tags: data.tags || [],
          requiredToolIds: data.requiredToolIds || [],
          requiredIntegrationTypes: data.requiredIntegrationTypes || [],
          scope: (data.scope as 'personal') || 'personal',
          visibility: data.visibility || 'public',
        },
      });

      toast({
        variant: 'success',
        title: t('CREATE_SKILL_TOAST_UPDATED_TITLE') || 'Skill updated',
        description:
          t('CREATE_SKILL_TOAST_UPDATED_DESCRIPTION') ||
          'Your skill has been updated successfully.',
      });

      setIsEditingDescription(false);
      setShowDiscardWarning(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: apiErrorMessage(
          error,
          t('FAILED_TO_UPDATE_SKILL', { defaultValue: 'Failed to update skill.' })
        ),
      });
    }
  };

  const handleDescriptionCancel = () => {
    setEditDescription(data?.description?.trim() || '');
    setIsEditingDescription(false);
  };

  const handleStartEditName = () => {
    setEditName(data?.name?.trim() || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    const trimmedName = editName.trim();
    if (!data || !canManage || !isNameDirty || !trimmedName) return;

    try {
      await updateSkill({
        skillId: data.id,
        payload: {
          name: trimmedName,
          description: data.description || '',
          instructions: data.instructions || '',
          version: data.version || '1.0.0',
          iconUrl: data.iconUrl,
          tags: data.tags || [],
          requiredToolIds: data.requiredToolIds || [],
          requiredIntegrationTypes: data.requiredIntegrationTypes || [],
          scope: (data.scope as 'personal') || 'personal',
          visibility: data.visibility || 'public',
        },
      });

      toast({
        variant: 'success',
        title: t('CREATE_SKILL_TOAST_UPDATED_TITLE') || 'Skill updated',
        description:
          t('CREATE_SKILL_TOAST_UPDATED_DESCRIPTION') ||
          'Your skill has been updated successfully.',
      });

      setIsEditingName(false);
      setShowDiscardWarning(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: apiErrorMessage(
          error,
          t('FAILED_TO_UPDATE_SKILL', { defaultValue: 'Failed to update skill.' })
        ),
      });
    }
  };

  const handleNameCancel = () => {
    setEditName(data?.name?.trim() || '');
    setIsEditingName(false);
  };

  const handleToolsChange = async (requiredToolIds: string[]) => {
    if (!data || !canManage) return;

    try {
      await updateSkill({
        skillId: data.id,
        payload: {
          name: data.name,
          description: data.description || '',
          instructions: data.instructions || '',
          version: data.version || '1.0.0',
          iconUrl: data.iconUrl,
          tags: data.tags || [],
          requiredToolIds,
          requiredIntegrationTypes: data.requiredIntegrationTypes || [],
          scope: (data.scope as 'personal') || 'personal',
          visibility: data.visibility || 'public',
        },
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: apiErrorMessage(
          error,
          t('FAILED_TO_UPDATE_SKILL', { defaultValue: 'Failed to update skill.' })
        ),
      });
    }
  };

  const handleFileSave = async () => {
    if (!data || !canManage || !activeFile) return;

    try {
      if (activeFile.path === 'SKILL.md') {
        await updateSkill({
          skillId: data.id,
          payload: {
            name: data.name,
            description: data.description || '',
            instructions: editContent,
            version: data.version || '1.0.0',
            iconUrl: data.iconUrl,
            tags: data.tags || [],
            requiredToolIds: data.requiredToolIds || [],
            requiredIntegrationTypes: data.requiredIntegrationTypes || [],
            scope: (data.scope as 'personal') || 'personal',
            visibility: data.visibility || 'public',
          },
        });
      } else if (activeFile.fileId) {
        await updateSkillResource({
          skillId: data.id,
          fileId: activeFile.fileId,
          content: editContent,
        });
        setCustomResources((prev) => {
          const base = prev ?? data.resources ?? [];
          return base.map((resource) =>
            resource.fileId === activeFile.fileId
              ? { ...resource, content: editContent, size: new Blob([editContent]).size }
              : resource
          );
        });
      } else {
        const updatedResources = currentResources.map((resource) =>
          resource.path === activeFile.path
            ? { ...resource, content: editContent, size: new Blob([editContent]).size }
            : resource
        );
        await updateSkill({
          skillId: data.id,
          payload: {
            name: data.name,
            description: data.description || '',
            instructions: data.instructions || '',
            version: data.version || '1.0.0',
            iconUrl: data.iconUrl,
            tags: data.tags || [],
            requiredToolIds: data.requiredToolIds || [],
            requiredIntegrationTypes: data.requiredIntegrationTypes || [],
            scope: (data.scope as 'personal') || 'personal',
            visibility: data.visibility || 'public',
            resources: updatedResources,
          },
        });
        setCustomResources(updatedResources);
      }

      toast({
        variant: 'success',
        title: t('CREATE_SKILL_TOAST_UPDATED_TITLE') || 'Skill updated',
        description:
          t('CREATE_SKILL_TOAST_UPDATED_DESCRIPTION') ||
          'Your skill has been updated successfully.',
      });

      setIsEditMode(false);
      setShowDiscardWarning(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR', { defaultValue: 'Error' }),
        description: apiErrorMessage(
          error,
          t('FAILED_TO_UPDATE_SKILL', { defaultValue: 'Failed to update skill.' })
        ),
      });
    }
  };

  const handleDiscardChanges = () => {
    if (pendingFile) {
      const fileExists = files.some((f) => f.path === pendingFile);
      if (fileExists) {
        setSelectedFile(pendingFile);
      }
      setPendingFile(null);
      setIsEditMode(false);
    } else if (pendingClose) {
      setPendingClose(false);
      setIsEditMode(false);
      setIsEditingDescription(false);
      setEditDescription('');
      setIsEditingName(false);
      setEditName('');
      setShowDiscardWarning(false);
      onClose();
      return;
    } else if (isNameDirty) {
      handleNameCancel();
    } else if (isDescriptionDirty) {
      handleDescriptionCancel();
    } else {
      setEditContent(activeFile?.content ?? '');
      setIsEditMode(false);
    }
    setShowDiscardWarning(false);
  };

  const handleCancelDiscard = () => {
    setShowDiscardWarning(false);
    setPendingFile(null);
    setPendingClose(false);
  };

  const previewEmptyContent =
    t('SKILL_DETAILS_NO_CONTENT') || '_No content available._';

  const renderFolderNode = (folder: SkillFolderNode<SkillFile>, depth: number) => {
    const isCollapsed = collapsedFolders.has(folder.path);
    const ChevronIcon = isCollapsed ? ChevronRight : ChevronDown;
    const indent = depth * 16;

    return (
      <div key={folder.path} className="flex min-w-0 shrink-0 flex-col lg:w-full">
        <div className="group flex w-full items-center justify-between min-w-0 px-1 py-0.5 text-sm text-muted-foreground">
          <button
            type="button"
            aria-expanded={!isCollapsed}
            title={folder.path}
            onClick={() => toggleFolder(folder.path)}
            className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1 text-left hover:text-foreground"
            style={{ paddingLeft: 8 + indent }}
          >
            <ChevronIcon className="h-3.5 w-3.5 shrink-0" />
            <Folder className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap truncate">{folder.name}</span>
          </button>
          {canManage && !isLearned && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 rounded p-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-background hover:text-foreground data-[state=open]:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                  <span className="sr-only">Folder options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="bottom"
                sideOffset={4}
                className="z-[200] min-w-0 w-44"
                onCloseAutoFocus={preventMenuFocusRestore}
              >
                <DropdownMenuItem
                  className="cursor-pointer gap-2"
                  onClick={() => openInlineFile(folder.path)}
                >
                  <FilePlus className="h-4 w-4 text-muted-foreground" />
                  <span>{t('NEW_FILE') || 'New file'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2"
                  onClick={() => openInlineFolder(folder.path)}
                >
                  <FolderPlus className="h-4 w-4 text-muted-foreground" />
                  <span>{t('NEW_FOLDER') || 'New folder'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2"
                  onClick={() => {
                    setTargetFolderForNew(folder.path);
                    setIsUploadModalOpen(true);
                  }}
                >
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <span>{t('UPLOAD_FILE') || 'Upload file'}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {!isCollapsed && (
          <div className="flex min-w-0 gap-0.5 lg:flex-col lg:space-y-0">
            {creatingInlineFile && creatingInlineFile.folder === folder.path && (
              <div
                className="flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm lg:min-w-0"
                style={{ marginLeft: 24 + indent, width: `calc(100% - ${24 + indent}px)` }}
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  ref={focusInlineNameInput}
                  type="text"
                  autoFocus
                  value={inlineFileName}
                  onChange={(e) => setInlineFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleInlineFileSubmit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setCreatingInlineFile(null);
                      setInlineFileName('');
                    }
                  }}
                  onBlur={() => {
                    if (Date.now() - inlineInputMountTimeRef.current < 400) return;
                    void handleInlineFileSubmit();
                  }}
                  placeholder={t('FILE_NAME') || 'notes.md or greet.py'}
                  className="h-6 w-full min-w-0 border-none bg-transparent p-0 text-sm text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                />
              </div>
            )}
            {creatingInlineFolder && creatingInlineFolder.parentFolder === folder.path && (
              <div
                className="flex w-full items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm lg:min-w-0"
                style={{ marginLeft: 24 + indent, width: `calc(100% - ${24 + indent}px)` }}
              >
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  ref={focusInlineNameInput}
                  type="text"
                  autoFocus
                  value={inlineFolderName}
                  onChange={(e) => setInlineFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleInlineFolderSubmit();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setCreatingInlineFolder(null);
                      setInlineFolderName('');
                    }
                  }}
                  onBlur={() => {
                    if (Date.now() - inlineInputMountTimeRef.current < 400) return;
                    void handleInlineFolderSubmit();
                  }}
                  placeholder={t('FOLDER_NAME') || 'Folder name...'}
                  className="h-6 w-full min-w-0 border-none bg-transparent p-0 text-sm text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                />
              </div>
            )}
            {folder.folders.map((child) => renderFolderNode(child, depth + 1))}
            {folder.files.map((file) => {
              const FileIcon = file.kind === 'code' ? FileCode : FileText;
              const isActive = file.path === activeFile?.path;

              return (
                <div key={file.path} className="group relative flex w-full items-center justify-between min-w-0 px-1 py-0.5">
                  <button
                    type="button"
                    title={file.path}
                    onClick={() => handleFileSelect(file.path)}
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 pr-2 text-left text-sm transition-colors',
                      isActive
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-background/80 hover:text-foreground'
                    )}
                    style={{ paddingLeft: 24 + indent }}
                  >
                    <FileIcon className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 truncate">{file.name}</span>
                  </button>

                  {canManage && !isLearned && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0 rounded p-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-background hover:text-foreground data-[state=open]:opacity-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                          <span className="sr-only">File options</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="z-[200] min-w-0 w-36">
                        <DropdownMenuItem
                          className="cursor-pointer gap-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileSelect(file.path);
                            setEditContent(file.content ?? '');
                            setIsEditMode(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                          <span>{t('EDIT_FILE') || 'Edit file'}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer gap-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenameFileTarget(file);
                            setIsRenameModalOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                          <span>{t('RENAME') || 'Rename'}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer gap-2 text-red-600 focus:bg-red-50 focus:text-red-600 dark:text-red-400 dark:focus:bg-red-950"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteFileTarget(file);
                            setIsDeleteModalOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                          <span>{t('DELETE_FILE') || 'Delete file'}</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        if (isDirty) {
          setPendingClose(true);
          setShowDiscardWarning(true);
        } else {
          onClose();
        }
      }
    }}>
      <DialogContent
        className="flex h-[min(92vh,100dvh-2rem)] max-h-[min(92vh,100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[1180px] flex-col overflow-hidden p-0"
        hideClose
        aria-describedby={undefined}
      >
        {isLoading || isError || !data ? (
          <DialogTitle className="sr-only">
            {t('SKILL_DETAILS_TITLE', { defaultValue: 'Skill details' })}
          </DialogTitle>
        ) : null}
        {isLoading ? (
          <SkillDetailsLoadingState />
        ) : isError || !data ? (
          <SkillDetailsErrorState
            onRetry={() => {
              void refetch();
            }}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
              <div className="min-w-0 flex-1">
                <div className={cn('flex gap-3', isEditingName ? 'items-start sm:items-center' : 'items-center')}>
                  <Avatar className="h-10 w-10 shrink-0 border border-border bg-muted/40">
                    {data.iconUrl ? <AvatarImage src={data.iconUrl} alt={data.name} /> : null}
                    <AvatarFallback className="bg-transparent text-xs font-semibold text-foreground">
                      {getSkillInitials(data.name)}
                    </AvatarFallback>
                  </Avatar>
                  {isEditingName ? (
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:flex-nowrap">
                      <DialogTitle className="sr-only">
                        {editName.trim() || data.name}
                      </DialogTitle>
                      <Input
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        maxLength={isLearned ? 128 : 64}
                        className="h-10 w-full min-w-0 max-w-md flex-1 basis-full rounded-nav text-lg font-semibold sm:h-9 sm:basis-auto sm:text-xl"
                        aria-label={t('SKILL_NAME', { defaultValue: 'Skill Name' })}
                      />
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-10 px-2.5 text-xs sm:h-7"
                          onClick={() => {
                            if (isNameDirty) {
                              setShowDiscardWarning(true);
                            } else {
                              handleNameCancel();
                            }
                          }}
                        >
                          {t('CANCEL') || 'Cancel'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="h-10 px-2.5 text-xs sm:h-7"
                          onClick={() => void handleNameSave()}
                          disabled={!isNameDirty || !editName.trim() || isUpdating}
                        >
                          {isUpdating ? t('SAVING', { defaultValue: 'Saving...' }) : t('SAVE') || 'Save'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                      <DialogTitle className="break-words text-lg font-semibold text-foreground sm:text-xl">
                        {data.name}
                      </DialogTitle>
                      {canManage ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                          onClick={handleStartEditName}
                          aria-label={t('EDIT_SKILL_NAME', { defaultValue: 'Edit skill name' })}
                        >
                          <Edit width={14} height={14} strokeWidth={1.75} />
                        </Button>
                      ) : null}

                      {data.installed && !isLearned ? (
                        <Badge variant="secondary" className="rounded-full px-2 py-0 text-[10px] font-medium">
                          {t('SKILLS_INSTALLED') || 'Installed'}
                        </Badge>
                      ) : null}
                    </div>
                  )}
                </div>
                {isEditingName ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 pl-[3.25rem]">
                    {data.installed && !isLearned ? (
                      <Badge variant="secondary" className="rounded-full px-2 py-0 text-[10px] font-medium">
                        {t('SKILLS_INSTALLED') || 'Installed'}
                      </Badge>
                    ) : null}
                  </div>
                ) : null}
                <div
                  className={cn(
                    'mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground',
                    isEditingName && 'pl-[3.25rem]'
                  )}
                >
                  {isLearned ? null : (
                    <>
                      <span>v{data.version || '1.0.0'}</span>
                      <span className="capitalize">{data.scope || 'personal'}</span>
                      <VisibilityBadge value={data.visibility} className="shrink-0" />
                    </>
                  )}
                  {source?.meta ? <span>{source.meta}</span> : null}
                  <span>
                    {formatDate(data.lastUpdatedDate) === '-'
                      ? t('SKILLS_UPDATED_EMPTY') || 'Updated —'
                      : t('SKILLS_UPDATED', { date: formatDate(data.lastUpdatedDate) }) ||
                      `Updated ${formatDate(data.lastUpdatedDate)}`}
                  </span>
                </div>
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                {source?.onDelete ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10 text-destructive sm:h-9"
                    onClick={source.onDelete}
                  >
                    {t('DELETE') || 'Delete'}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 w-full shrink-0 px-3 sm:h-9 sm:w-auto"
                  onClick={() => {
                    if (isDirty) {
                      setPendingClose(true);
                      setShowDiscardWarning(true);
                    } else {
                      onClose();
                    }
                  }}
                >
                  <span>{t('CLOSE') || 'Close'}</span>
                </Button>
              </div>
            </div>

            {source?.banner}
            {source?.note ? (
              <p className="border-b border-border px-4 py-3 text-xs leading-body text-muted-foreground sm:px-5">
                {source.note}
              </p>
            ) : null}

            <div className="flex min-h-0 flex-1 overflow-hidden">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
                <aside className="flex min-h-0 w-full shrink-0 flex-col overflow-hidden border-b bg-muted/20 lg:w-60 lg:border-b-0 lg:border-r">
                  <div className="flex min-h-0 flex-1 flex-col p-1.5">
                    <div className="mb-1 mt-2 flex shrink-0 items-center justify-between pl-2 pr-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {t('SKILL_DETAILS_FILES') || 'Files'}
                      </p>
                      {canManage && !isLearned && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 rounded p-0 text-muted-foreground hover:bg-background hover:text-foreground"
                            >
                              <Plus className="h-4 w-4" />
                              <span className="sr-only">{t('ADD_FILE_OR_FOLDER') || 'Add file or folder'}</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            side="bottom"
                            sideOffset={4}
                            className="z-[200] min-w-0 w-44"
                            onCloseAutoFocus={preventMenuFocusRestore}
                          >
                            <DropdownMenuItem
                              onClick={() => openInlineFile('')}
                              className="cursor-pointer gap-2"
                            >
                              <FilePlus className="h-4 w-4 text-muted-foreground" />
                              <span>{t('NEW_FILE') || 'New file'}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openInlineFolder('')}
                              className="cursor-pointer gap-2"
                            >
                              <FolderPlus className="h-4 w-4 text-muted-foreground" />
                              <span>{t('NEW_FOLDER') || 'New folder'}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setIsUploadModalOpen(true)} className="cursor-pointer gap-2">
                              <Upload className="h-4 w-4 text-muted-foreground" />
                              <span>{t('UPLOAD_FILE') || 'Upload file'}</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    <div className="flex min-h-0 flex-1 gap-0.5 overflow-x-auto overflow-y-auto lg:flex-col lg:space-y-0">
                      {creatingInlineFile && creatingInlineFile.folder === '' && (
                        <div className="flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm lg:w-full lg:min-w-0">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <input
                            ref={focusInlineNameInput}
                            type="text"
                            autoFocus
                            value={inlineFileName}
                            onChange={(e) => setInlineFileName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleInlineFileSubmit();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setCreatingInlineFile(null);
                                setInlineFileName('');
                              }
                            }}
                            onBlur={() => {
                              if (Date.now() - inlineInputMountTimeRef.current < 400) return;
                              void handleInlineFileSubmit();
                            }}
                            placeholder={t('FILE_NAME') || 'notes.md or greet.py'}
                            className="h-6 w-full min-w-0 border-none bg-transparent p-0 text-sm text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                          />
                        </div>
                      )}
                      {creatingInlineFolder && creatingInlineFolder.parentFolder === '' && (
                        <div className="flex w-full items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm lg:w-full lg:min-w-0">
                          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <input
                            ref={focusInlineNameInput}
                            type="text"
                            autoFocus
                            value={inlineFolderName}
                            onChange={(e) => setInlineFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleInlineFolderSubmit();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                setCreatingInlineFolder(null);
                                setInlineFolderName('');
                              }
                            }}
                            onBlur={() => {
                              if (Date.now() - inlineInputMountTimeRef.current < 400) return;
                              void handleInlineFolderSubmit();
                            }}
                            placeholder={t('FOLDER_NAME') || 'Folder name...'}
                            className="h-6 w-full min-w-0 border-none bg-transparent p-0 text-sm text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                          />
                        </div>
                      )}
                      {rootFiles.map((file) => {
                        const isActive = file.path === activeFile?.path;

                        return (
                          <div key={file.path} className="group relative flex w-full items-center justify-between min-w-0 px-1 py-0.5">
                            <button
                              type="button"
                              title={file.name}
                              onClick={() => handleFileSelect(file.path)}
                              className={cn(
                                'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-left text-sm transition-colors',
                                isActive
                                  ? 'bg-background text-foreground shadow-sm'
                                  : 'text-muted-foreground hover:bg-background/80 hover:text-foreground'
                              )}
                            >
                              <FileText className="h-4 w-4 shrink-0" />
                              <span className="min-w-0 truncate">{file.name}</span>
                            </button>

                            {canManage && !isLearned && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0 rounded p-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-background hover:text-foreground data-[state=open]:opacity-100"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                    <span className="sr-only">File options</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="z-[200] min-w-0 w-36">
                                  <DropdownMenuItem
                                    className="cursor-pointer gap-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFileSelect(file.path);
                                      setEditContent(file.content ?? '');
                                      setIsEditMode(true);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4 text-muted-foreground" />
                                    <span>{t('EDIT_FILE') || 'Edit file'}</span>
                                  </DropdownMenuItem>
                                  {file.path !== 'SKILL.md' && (
                                    <>
                                      <DropdownMenuItem
                                        className="cursor-pointer gap-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRenameFileTarget(file);
                                          setIsRenameModalOpen(true);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4 text-muted-foreground" />
                                        <span>{t('RENAME') || 'Rename'}</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="cursor-pointer gap-2 text-red-600 focus:bg-red-50 focus:text-red-600 dark:text-red-400 dark:focus:bg-red-950"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteFileTarget(file);
                                          setIsDeleteModalOpen(true);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                                        <span>{t('DELETE_FILE') || 'Delete file'}</span>
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        );
                      })}

                      {folderTree.map((folder) => renderFolderNode(folder, 0))}
                    </div>
                  </div>
                  {isLearned ? null : (
                    <div className="mt-auto flex min-h-[140px] shrink-0 flex-col border-t border-border p-1.5 pt-3 lg:h-[30%]">
                      <SkillToolsSection
                        selectedToolIds={data.requiredToolIds || []}
                        onChange={(ids) => void handleToolsChange(ids)}
                        disabled={!canManage}
                      />
                    </div>
                  )}
                </aside>

                <div className={cn('min-h-0 min-w-0 flex-1 overflow-y-auto', isEditMode && 'flex flex-col')}>
                  {!isEditMode && (
                    <div className="space-y-4 border-b px-4 py-4 sm:px-6 sm:py-5">
                      <div className="min-w-0">
                        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                          <p className="min-w-0 break-words text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {t('SKILL_DETAILS_DESCRIPTION_LABEL', { defaultValue: 'Description' })}
                          </p>
                          {canManage && !isEditingDescription ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                              onClick={handleStartEditDescription}
                              aria-label={t('EDIT_DESCRIPTION', { defaultValue: 'Edit description' })}
                            >
                              <Edit width={14} height={14} strokeWidth={1.75} />
                            </Button>
                          ) : null}
                          {canManage && isEditingDescription ? (
                            <div className="mb-2 ml-auto flex shrink-0 items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 px-2.5 text-xs"
                                onClick={() => {
                                  if (isDescriptionDirty) {
                                    setShowDiscardWarning(true);
                                  } else {
                                    handleDescriptionCancel();
                                  }
                                }}
                              >
                                {t('CANCEL') || 'Cancel'}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="h-7 px-2.5 text-xs"
                                onClick={() => void handleDescriptionSave()}
                                disabled={!isDescriptionDirty || isUpdating}
                              >
                                {isUpdating ? t('SAVING', { defaultValue: 'Saving...' }) : t('SAVE') || 'Save'}
                              </Button>
                            </div>
                          ) : null}
                        </div>
                        {isEditingDescription ? (
                          <Textarea
                            value={editDescription}
                            onChange={(event) => setEditDescription(event.target.value)}
                            maxLength={1024}
                            placeholder={
                              t('CREATE_SKILL_DESCRIPTION_PLACEHOLDER') ||
                              'Generate weekly status reports from recent work. Use when asked for updates or progress summaries.'
                            }
                            className="min-h-[100px] rounded-[0.5rem] border-border px-4 py-3 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
                          />
                        ) : (
                          <p className="break-words text-sm leading-7 text-foreground">{skillDescription}</p>
                        )}
                      </div>

                      {/* <div className="flex flex-wrap gap-2">
                        {(data.tags?.length ? data.tags : [t('SKILLS_UNTAGGED') || 'untagged']).map(
                          (tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="rounded-full px-2.5 py-0.5 text-xs font-normal"
                            >
                              {tag}
                            </Badge>
                          )
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-3 text-sm text-muted-foreground sm:grid-cols-2 xl:grid-cols-3">
                        <div className="rounded-lg border border-border/70 bg-background px-3 py-2">
                          <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide">
                            <Wrench className="h-3.5 w-3.5" />
                            {t('SKILL_DETAILS_TOOLS_ADDED') || 'Tools added'}
                          </div>
                          <p>
                            {data.requiredToolIds.length > 0
                              ? data.requiredToolIds.length
                              : t('SKILL_DETAILS_NONE') || 'None'}
                          </p>
                        </div> */}
                      {/* <div className="rounded-lg border border-border/70 bg-background px-3 py-2">
                        <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide">
                          <Puzzle className="h-3.5 w-3.5" />
                          {t('SKILL_DETAILS_INTEGRATIONS') || 'Integrations'}
                        </div>
                        <p>
                          {data.requiredIntegrationTypes.length > 0
                            ? data.requiredIntegrationTypes.join(', ')
                            : t('SKILL_DETAILS_NONE') || 'None'}
                        </p>
                      </div> */}
                      {/* <div className="rounded-lg border border-border/70 bg-background px-3 py-2">
                          <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide">
                            <Layers3 className="h-3.5 w-3.5" />
                            {t('SKILL_DETAILS_INSTALL_COUNT') || 'Install count'}
                          </div>
                          <p>{data.installCount}</p>
                        </div>
                      </div> */}
                    </div>
                  )}

                  <div className={cn('min-w-0 px-4 py-4 sm:px-6 sm:py-6', isEditMode && 'flex flex-1 flex-col h-full overflow-y-auto')}>
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('SKILL_DETAILS_PREVIEW') || 'Preview'}
                        </p>
                        <h3 className="truncate text-sm font-medium text-foreground">{activeFile?.name}</h3>
                      </div>
                      {canManage && (
                        <div className="flex shrink-0 items-center gap-2">
                          {isEditMode ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-3"
                                onClick={() => {
                                  if (isDirty) {
                                    setShowDiscardWarning(true);
                                  } else {
                                    setIsEditMode(false);
                                  }
                                }}
                              >
                                {t('CANCEL') || 'Cancel'}
                              </Button>
                              <Button
                                size="sm"
                                className="h-8 px-3"
                                onClick={handleFileSave}
                                disabled={!isDirty || isUpdating || isUpdatingResource}
                              >
                                {isUpdating || isUpdatingResource ? 'Saving...' : (t('SAVE') || 'Save')}
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1.5 rounded-[62px] border-[#D9DBE9] bg-gradient-to-br from-white to-[#FBFBFE] px-3.5 text-xs font-medium leading-[115%] text-[#170F49] shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground"
                              onClick={() => {
                                setEditContent(activeFile?.content ?? '');
                                setIsEditMode(true);
                              }}
                            >
                              <Edit width={14} height={14} strokeWidth={1.75} />
                              {t('EDIT_FILE') || 'Edit File'}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <Separator className="-mx-4 mb-6 w-auto sm:-mx-6" />

                    {isEditMode ? (
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        maxLength={isLearned ? 50000 : undefined}
                        className="h-full w-full flex-1 resize-none bg-transparent font-mono text-base text-foreground outline-none placeholder:text-muted-foreground md:text-sm"
                        spellCheck={false}
                      />
                    ) : (
                      <div className="prose prose-sm max-w-none break-words dark:prose-invert [&_ol]:pl-6 [&_ul]:pl-6">
                        <MarkdownRenderer content={renderFileContent(activeFile) || previewEmptyContent} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <DiscardChangesModal
        open={showDiscardWarning}
        fileName={activeFile?.name}
        onCancel={handleCancelDiscard}
        onDiscard={handleDiscardChanges}
      />

      <NewFileModal
        open={isNewFileModalOpen}
        onOpenChange={(open) => {
          setIsNewFileModalOpen(open);
          if (!open) setTargetFolderForNew('');
        }}
        existingFolders={existingFolderNames}
        initialFolder={targetFolderForNew}
        onSave={handleCreateNewFile}
        isPending={isUpdating}
      />
      <NewFolderModal
        open={isNewFolderModalOpen}
        onOpenChange={setIsNewFolderModalOpen}
        onSave={handleCreateNewFolder}
        isPending={isUpdating}
      />
      <UploadFileModal
        open={isUploadModalOpen}
        onOpenChange={(open) => {
          setIsUploadModalOpen(open);
          if (!open) setTargetFolderForNew('');
        }}
        existingFolders={existingFolderNames}
        initialFolder={targetFolderForNew}
        onUpload={handleUploadFiles}
        isPending={isUpdating}
      />
      <RenameFileModal
        open={isRenameModalOpen}
        onOpenChange={(open) => {
          setIsRenameModalOpen(open);
          if (!open) setRenameFileTarget(null);
        }}
        file={renameFileTarget}
        onRename={handleRenameFile}
        isPending={isUpdating}
      />
      <DeleteFileModal
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open);
          if (!open) setDeleteFileTarget(null);
        }}
        file={deleteFileTarget}
        onDelete={handleDeleteFile}
        isPending={isUpdating}
      />
    </Dialog>
  );
}
